// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import {selx} from 'clsx';

import {nameToInitials} from 'common/util/string';
import {TooltipEl} from 'common/components/tooltip';

import type {UserIcon} from './index';
import emptyIconImage from './empty-icon.svg';
import mpAvatarUrl from './mp-avatar.svg';
import styles from './index.module.styl';

const ImageIcon = ({
  src,
  iconSize,
  handleAvatarImageLoaded,
  elref,
  isAvatarImageLoaded = true,
  isStaffIcon = false,
}: {
  src: string;
  iconSize: number;
  handleAvatarImageLoaded?: (ev: Event) => void;
  elref: string;
  isAvatarImageLoaded?: boolean;
  isStaffIcon?: boolean;
}) => (
  <div sel={selx(styles.imageIcon, styles.colorAvatar)} class={{[styles.loading]: !isAvatarImageLoaded}}>
    <img
      sel={selx(styles.img)}
      attrs={{
        elref,
        align: `middle`,
        src,
        loading: `lazy`,
      }}
      style={{
        height: isStaffIcon ? `${iconSize * (2 / 3)}px` : `${iconSize}px`,
        width: isStaffIcon ? `${iconSize * (2 / 3)}px` : `${iconSize}px`,
        ...(isStaffIcon ? {margin: `${iconSize / 6}px`} : {}),
      }}
      on={{
        load: handleAvatarImageLoaded,
      }}
    />
  </div>
);

const InitialsIcon = ({name, backgroundClassName}: {name: string; backgroundClassName: string}) => (
  <div class={{[backgroundClassName]: true, [styles.initialsIcon]: true}} attrs={{elref: `initialsIcon`}}>
    {nameToInitials(name)}
  </div>
);

const Placeholder = ({userIcon, iconSize, name}: {userIcon: UserIcon; iconSize: number; name: string}) => {
  if (name) {
    return <InitialsIcon name={name} backgroundClassName={userIcon.helpers.getBackgroundColorClass()} />;
  }
  return <ImageIcon src={emptyIconImage} iconSize={iconSize} isAvatarImageLoaded elref='defaultImg' />;
};

const Avatar = ({userIcon, iconSize, name}: {userIcon: UserIcon; iconSize: number; name: string}) => {
  const avatarUrl = userIcon.helpers.getAvatarUrl();
  const {isAvatarImageLoaded} = userIcon.state;
  if (avatarUrl) {
    return (
      <>
        <ImageIcon
          src={avatarUrl}
          iconSize={iconSize}
          isAvatarImageLoaded={isAvatarImageLoaded}
          elref='userImg'
          handleAvatarImageLoaded={userIcon.helpers.handleAvatarImageLoaded}
          isStaffIcon={userIcon.shouldShowStaffAvatar()}
        />
        {isAvatarImageLoaded ? null : <Placeholder userIcon={userIcon} iconSize={iconSize} name={name} />}
      </>
    );
  }
  return <Placeholder userIcon={userIcon} iconSize={iconSize} name={name} />;
};

export function template(this: UserIcon) {
  const {iconSize, fontSize} = this.helpers.getIconProperties();
  const name = this.helpers.getDisplayName();
  const labelPrefix = this.attr(`label-prefix`);
  const labelSuffix = this.attr(`label-suffix`);

  const label = `${labelPrefix} ${name} ${labelSuffix}`.trim();

  return (
    <div
      class={{
        [styles.clickable]: this.helpers.shouldOpenSidebar(),
        [styles.container]: true,
      }}
      attrs={{tabindex: 0}}
      on={{
        click: this.helpers.handleIconClick,
        keypress: this.helpers.handleIconClick,
      }}
    >
      <div
        sel={selx(styles.avatarContainer)}
        style={{
          'font-size': `${fontSize}px`,
          height: `${iconSize}px`,
          'line-height': `${iconSize}px`,
          width: `${iconSize}px`,
          'min-width': `${iconSize}px`,
        }}
        class={{
          [styles.isStaffMemberImage]: !!this.attr(`staff-member-avatar-uuid`),
          [styles.offline]: this.attr(`is-offline`),
        }}
      >
        <Avatar userIcon={this} iconSize={iconSize} name={name} />
        {this.attr(`staff-member-avatar-uuid`) && (
          <img sel={selx(styles.staffMemberImageOverlay, styles.img)} attrs={{src: mpAvatarUrl}} />
        )}
      </div>
      {!this.attr(`disable-tooltip`) && this.attr(`name`) && (
        <TooltipEl attrs={{placement: this.attr(`tooltip-placement`), offset: 12}} sel={selx(styles.mpTooltip)}>
          {labelPrefix && <div sel={selx(styles.tooltipSubtitle)}>{labelPrefix}</div>}
          <div sel={selx(styles.tooltipText)}>{name}</div>
          {labelSuffix && <div sel={selx(styles.tooltipSubtitle)}>{labelSuffix}</div>}
        </TooltipEl>
      )}
      {this.attr(`include-label`) ? (
        <div sel={selx(styles.label)} attrs={{elref: `label`}} style={{'line-height': `${iconSize}px`}}>
          {label}
        </div>
      ) : null}
    </div>
  );
}
